import { z } from "zod";
import { brandSchema, websiteSchema } from "./brands.js";
/**
 * The brand-report boundary.
 *
 * A report owns no captures. It names one brand, a set of competitors, and the
 * questions it is scored on — and the same capture is scored independently for
 * every report subscribing to that question. That is why reports are free and
 * unlimited while prompts are metered.
 */
export const reportSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    status: z.enum(["setup", "active"]),
    brand: brandSchema,
    competitors: z.array(brandSchema),
    /**
     * Questions the report is tracking now. Paused ones are not counted, and
     * zero is a real report: it keeps the history it already has.
     */
    promptCount: z.number().int().min(0),
    /**
     * The whole report is paused: it tracks no question now, though it has
     * tracked some. Its answers so far stay; nothing new is captured. A report
     * that never had a question is not paused, it is empty.
     */
    paused: z.boolean(),
});
export const reportListSchema = z.object({ reports: z.array(reportSchema) });
export const createReportSchema = z.object({
    name: z.string().trim().min(1).max(120),
    brandId: z.string().min(1),
});
/**
 * Renaming a report.
 *
 * The title is the report's, not the brand's. They start the same and drift on
 * purpose: one workspace watches a brand once and never touches this, another
 * runs "GitHub — enterprise buyers" beside "GitHub — students", and both are
 * about the same company.
 */
export const updateReportSchema = z.object({
    name: z.string().trim().min(1).max(120),
});
/** What deleting a report took with it. */
export const deletedReportSchema = z.object({
    /** False when another report still uses the brand, so its data stayed. */
    brandDeleted: z.boolean(),
    /** Captures still queued that no remaining report reads. */
    capturesCancelled: z.number().int().min(0),
});
export const competitorCandidateSchema = z.object({
    name: z.string().trim().min(1).max(120),
    website: websiteSchema,
});
export const addCompetitorsSchema = z.object({
    competitors: z.array(competitorCandidateSchema).min(1).max(50),
});
/**
 * What happened to one suggested competitor.
 *
 * Four outcomes, and the last two are the reason this type is not a boolean.
 * A run that adds three and refuses two has to be able to say which two, and
 * why, or the customer sees "3 added" and assumes the other two failed.
 */
export const competitorOutcomeSchema = z.discriminatedUnion("outcome", [
    /** A new brand was created and added to the report. */
    z.object({
        outcome: z.literal("added"),
        brand: brandSchema,
        /**
         * Mentions this competitor already had, linked the moment they were added.
         *
         * Every answer was read for every company it named, whether or not anyone
         * was tracking them, so a competitor added today arrives with the history
         * that was already there. Zero is a real answer and a different one from
         * absent: it means we have read answers and none of them named this
         * company, which is itself worth knowing (§1).
         */
        backfilledMentions: z.number().int().min(0),
    }),
    /**
     * Already on this report. Not an error — this is what makes running Suggest
     * Competitors twice add nothing the second time.
     */
    z.object({ outcome: z.literal("already_listed"), brand: brandSchema }),
    /**
     * The report's own brand. Silently allowing it would give the report a
     * perfect score against itself, forever.
     */
    z.object({ outcome: z.literal("is_the_subject"), brand: brandSchema }),
    /**
     * The name matches a brand we hold but the site does not. Nothing was
     * written; a person picks.
     */
    z.object({
        outcome: z.literal("needs_a_decision"),
        name: z.string(),
        website: z.string(),
        candidates: z.array(brandSchema),
    }),
    /** No usable domain in what was suggested. Nothing was written. */
    z.object({
        outcome: z.literal("unusable"),
        name: z.string(),
        website: z.string(),
        reason: z.string(),
    }),
]);
/**
 * Taking questions off brand reports, several of each at once.
 *
 * The questions stay in the library. A report that would be left with none is
 * refused, because a report with no questions measures nothing.
 */
export const removeReportPromptsSchema = z.object({
    reportIds: z.array(z.string().min(1)).min(1).max(50),
    promptIds: z.array(z.string().min(1)).min(1).max(500),
});
export const removedReportPromptsSchema = z.object({
    /** Links paused, counted per report. Already-paused links are not counted again. */
    removed: z.number().int().min(0),
    /** Captures still in the queue that no tracking report reads now. */
    capturesCancelled: z.number().int().min(0),
});
export const addCompetitorsResultSchema = z.object({
    results: z.array(competitorOutcomeSchema),
});
export const subscribePromptsSchema = z.object({
    promptIds: z.array(z.string().min(1)).min(1).max(500),
});
export const subscriptionResultSchema = z.object({
    /** Prompts the report now tracks — the total, not the delta. */
    promptCount: z.number().int().min(0),
    /** Newly on the report, or resumed after a pause. */
    added: z.number().int().min(0),
    /** Already tracked. Repeating a subscribe call is safe and says so. */
    alreadySubscribed: z.number().int().min(0),
});
//# sourceMappingURL=reports.js.map